<template>
    <div v-if="!isExport">
        <b-row>
            <b-col md="9">
                <div class="monthly-exports-chart-container">
                    <chart-loader :loading="loading.monthlyExports"></chart-loader>
                    <monthly-exports
                        :options="chartOptions"
                        :chart-data="chartdata"
                    ></monthly-exports>
                </div>
            </b-col>

            <b-col md="3">
                <b-form
                    v-if="
                        !isExport &&
                        showControls &&
                        (loading.collections ||
                            loading.manufacturers ||
                            loading.materials)
                    "
                    inline
                    class="d-inline-block chart-filter mb-3 border shadow-sm rounded"
                >
                    <b-form-select
                        class="mb-2"
                        :value="null"
                        v-model="filters.manufacturer"
                        :options="manufacturers"
                    >
                        <template v-slot:first>
                            <option :value="null">Manufacturer</option>
                        </template>
                    </b-form-select>

                    <b-form-select
                        class="mb-2"
                        :value="null"
                        v-model="filters.material"
                        :options="materials"
                    >
                        <template v-slot:first>
                            <option :value="null">Material</option>
                        </template>
                    </b-form-select>

                    <b-form-select
                        class="mb-2"
                        :value="null"
                        v-model="filters.collection"
                        :options="formCollections"
                    >
                        <template v-slot:first>
                            <option :value="null">Collection</option>
                        </template>
                    </b-form-select>

                    <b-button variant="outline-primary" @click="clearFilter">Clear</b-button>
                </b-form>
            </b-col>
        </b-row>
    </div>

    <div v-else>
        <b-row>
            <b-col md="12">
                <div class="monthly-exports-chart-container">
                    <chart-loader :loading="loading.monthlyExports"></chart-loader>
                    <monthly-exports
                        :height="210"
                        v-if="isExport"
                        :options="chartOptions"
                        :chart-data="chartdata"
                    ></monthly-exports>
                </div>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import MonthlyExports from "../Charts/MonthlyExports";
import ChartLoader from "../Charts/ChartLoader";
import { mapActions, mapState, mapGetters } from "vuex";

export default {
  name: "MonthlyExportsContainer",
  components: { MonthlyExports, ChartLoader },
  props: {
    showControls: {
      default: true,
      type: Boolean
    },
    base_product: {
      default: null,
      type: Number
    },
    product_variant: {
      default: null,
      type: Number
    },
    isExport: {
      default: false,
      type: Boolean
    },
    range: {
      default: null,
      type: String
    }
  },
  data() {
    return {
      filters: {
        manufacturer: null,
        material: null,
        collection: null,
        base_product: this.base_product,
        product_variant: this.product_variant
      },
      chartOptions: {
        title: {
          display: false
        },
        tooltips: {
          mode: "index",
          intersect: false
        },
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          xAxes: [
            {
              stacked: true
            }
          ],
          yAxes: [
            {
              stacked: true
            }
          ]
        }
      }
    };
  },
  computed: {
    ...mapGetters({
      manufacturers: "analytics/getManufacturersForFormSelect",
      materials: "analytics/getMaterialsForFormSelect",
      chartdata: "analytics/getMonthlyExportsChartData"
    }),
    ...mapState("analytics", {
      start_date: state => state.start_date,
      end_date: state => state.end_date,
      unit: state => state.unit,
      loading: state => state.loading,
      collections: state => state.collections,
      client_id: state => state.clientIdFilter
    }),
    formCollections() {
      return this.collections.map(c => {
        let text = this.client_id
          ? c.name
          : c.name + " (" + c.client.name + ")";
        return {
          text: text,
          value: c.id
        };
      });
    }
  },
  watch: {
    filters: {
      handler() {
        this.fetchData();
      },
      deep: true
    },
    product_variant(n, o) {
      this.filters.product_variant = n;
    },
    base_product(n, o) {
      this.filters.base_product = n;
    }
  },
  mounted() {
    if (!this.isExport) {
      if (this.showControls) {
        this.getManufacturers({
          start_date: this.start_date,
          end_date: this.end_date,
          unit: this.unit
        });
        this.getCollections({
          start_date: this.start_date,
          end_date: this.end_date,
          unit: this.unit
        });
        this.getMaterials({
          start_date: this.start_date,
          end_date: this.end_date,
          unit: this.unit
        });
        this.getProblemCategories({
          start_date: this.start_date,
          end_date: this.end_date,
          unit: this.unit
        });
      }

      this.fetchData();

      this.$root.$on("montlyExp", () => {
        this.reRender();
      });
    }
  },
  methods: {
    ...mapActions({
      getManufacturers: "analytics/getManufacturers",
      getCollections: "analytics/getCollections",
      getMaterials: "analytics/getMaterials",
      getProblemCategories: "analytics/getProblemCategories",
      getMonthlyExports: "analytics/getMonthlyExports"
    }),
    fetchData() {
      this.getMonthlyExports({
        ...this.filters,
        start_date: this.start_date,
        end_date: this.end_date,
        unit: this.unit
      });
    },
    reRender() {
      if (this.showControls) {
        this.getManufacturers({
          start_date: this.start_date,
          end_date: this.end_date,
          unit: this.unit
        });
        this.getCollections({
          start_date: this.start_date,
          end_date: this.end_date,
          unit: this.unit
        });
        this.getMaterials({
          start_date: this.start_date,
          end_date: this.end_date,
          unit: this.unit
        });
        this.getProblemCategories({
          start_date: this.start_date,
          end_date: this.end_date,
          unit: this.unit
        });
      }

      this.$root.$emit("montlyExpChart");
      this.fetchData();
    },
    clearFilter() {
      this.filters = {
        manufacturer: null,
        material: null,
        collection: null,
        base_product: this.base_product,
        product_variant: this.product_variant
      };
    }
  }
};
</script>

<style scoped>
.form-inline .custom-select,
.form-inline .btn {
  width: 100%;
}
</style>
